import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DD,
  DT,
  GridCol,
  GridRow,
  isDefinedString,
  Text,
} from '@client/toolkit';
import { useGetTenantsCurrentCompanyQuery } from '@client/api';
import { useTenants } from '../../../hooks/useTenants/useTenants';
import { TenantSelectionEnum } from '../../../redux';

export const ProfileCompany = () => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tCompanies } = useTranslation('companies');
  const { t: tType } = useTranslation('type');

  const { selectedTenant } = useTenants();

  // Get tenant info
  const tenant = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant : null;
  
  // Fetch company for the current tenant
  const { data: company, isFetching, isError } = useGetTenantsCurrentCompanyQuery(undefined, {
    skip: !tenant,
  });
  
  // If there's an error (e.g., no company associated with tenant), treat as no company
  const hasCompany = !isError && !!company;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" variant="default">
          <Text
            className="text-xl font-medium text-white"
            loading={isFetching}
            isEmpty={!tenant?.name}
          >
            {tenant?.name}
          </Text>
        </CardTitle>
        </CardHeader>

      <CardContent>
        <div className="mb-6">
          <Text
            className="font-bold uppercase text-xs tracking-tight text-brand-primary mb-4"
          >
            {tCompanies('invoicingInformation')}
          </Text>

          <GridRow cols={3} spacingY={2} spacingX={2}>
            <GridCol span={1}>
              <DT>{tGlobal('legalPerson.vatId')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.vatId)}>
                {company?.vatId}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tGlobal('legalPerson.invoicingAccount.integration')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.invoicingAccount?.integration)}>
                {company?.invoicingAccount?.integration ? tType(`InvoicingIntegration.${company.invoicingAccount.integration}`) : '-'}
              </DD>
            </GridCol>
          </GridRow>
        </div>

        <div className="mb-6">
          <Text
            className="font-bold uppercase text-xs tracking-tight text-brand-primary mb-4"
          >
            {tCompanies('contactInformation')}
          </Text>

          <GridRow cols={3} spacingY={2} spacingX={2}>
            <GridCol span={1}>
              <DT>{tCompanies('form.website')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.website)}>
                {company?.website && (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-primary hover:underline"
                  >
                    {company.website}
                  </a>
                )}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.emailAddress')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.emailAddress)}>
                {company?.emailAddress && (
                  <a href={`mailto:${company.emailAddress}`} className="text-brand-primary hover:underline">
                    {company.emailAddress}
                  </a>
                )}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.mobilePhone')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.mobilePhone)}>
                {company?.mobilePhone && (
                  <a
                    href={`tel:${company.mobilePhone.replace(/\s/g, '')}`}
                    className="text-brand-primary hover:underline"
                  >
                    {company.mobilePhone}
                  </a>
                )}
              </DD>
            </GridCol>
          </GridRow>
        </div>

        <div className="mb-6">
          <GridRow cols={3} spacingY={2} spacingX={2}>
            <GridCol span={1}>
              <DT>{tCompanies('form.address')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.businessAddress?.address)}>
                {company?.businessAddress?.address}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.postalCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.businessAddress?.postalCode)}>
                {company?.businessAddress?.postalCode}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.postalCity')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.businessAddress?.city)}>
                {company?.businessAddress?.city}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.municipality')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.businessAddress?.municipality)}>
                {company?.businessAddress?.municipality}
              </DD>
            </GridCol>
          </GridRow>
        </div>

        <div className="mb-6">
          <Text
            className="font-bold uppercase text-xs tracking-tight text-brand-primary mb-4"
          >
            {tCompanies('companyInformation')}
          </Text>

          <GridRow cols={3} spacingY={2} spacingX={2}>
            <GridCol span={1}>
              <DT>{tCompanies('form.organizationForm')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.organizationForm)}>
                {company?.organizationForm}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.industryCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.industryCode)}>
                {company?.industryCode}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.institutionalSectorCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.institutionalSectorCode)}>
                {company?.institutionalSectorCode}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.numberOfEmployees')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || company?.numberOfEmployees == null}>
                {company?.numberOfEmployees}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.parentOrganizationNumber')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.parentOrganizationNumber)}>
                {company?.parentOrganizationNumber}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.entityRegistrationDate')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.entityRegistrationDate)}>
                {company?.entityRegistrationDate}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.isPartOfGroup')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || company?.isPartOfGroup == null}>
                {company?.isPartOfGroup == null
                  ? null
                  : company.isPartOfGroup
                  ? tCompanies('form.yes')
                  : tCompanies('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.languageCode')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.languageCode)}>
                {company?.languageCode}
              </DD>
            </GridCol>
          </GridRow>
        </div>

        <div className="mb-6">
          <Text
            className="font-bold uppercase text-xs tracking-tight text-brand-primary mb-4"
          >
            {tCompanies('statusInformation')}
          </Text>

          <GridRow cols={3} spacingY={2} spacingX={2}>
            <GridCol span={1}>
              <DT>{tCompanies('form.isBankrupt')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || company?.isBankrupt == null}>
                {company?.isBankrupt == null
                  ? null
                  : company.isBankrupt
                  ? tCompanies('form.yes')
                  : tCompanies('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.isUnderLiquidation')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || company?.isUnderLiquidation == null}>
                {company?.isUnderLiquidation == null
                  ? null
                  : company.isUnderLiquidation
                  ? tCompanies('form.yes')
                  : tCompanies('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.isUnderForcedDissolution')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || company?.isUnderForcedDissolution == null}>
                {company?.isUnderForcedDissolution == null
                  ? null
                  : company.isUnderForcedDissolution
                  ? tCompanies('form.yes')
                  : tCompanies('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.registeredInVatRegistry')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || company?.registeredInVatRegistry == null}>
                {company?.registeredInVatRegistry == null
                  ? null
                  : company.registeredInVatRegistry
                  ? tCompanies('form.yes')
                  : tCompanies('form.no')}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.vatRegistrationDate')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.vatRegistrationDate)}>
                {company?.vatRegistrationDate}
              </DD>
            </GridCol>
          </GridRow>
        </div>

        <div className="mb-6">
          <Text
            className="font-bold uppercase text-xs tracking-tight text-brand-primary mb-4"
          >
            {tCompanies('annualAccountsInformation')}
          </Text>

          <GridRow cols={3} spacingY={2} spacingX={2}>
            <GridCol span={1}>
              <DT>{tCompanies('form.lastAnnualAccountsYear')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.lastAnnualAccountsYear)}>
                {company?.lastAnnualAccountsYear}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.activity')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.activity)}>
                {company?.activity}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.remarks')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.remarks)}>
                {company?.remarks}
              </DD>
            </GridCol>

            <GridCol span={1}>
              <DT>{tCompanies('form.brregUrl')}</DT>
            </GridCol>
            <GridCol span={2}>
              <DD loading={isFetching} isEmpty={!hasCompany || !isDefinedString(company?.brregUrl)}>
                {company?.brregUrl && (
                  <a
                    href={company.brregUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-primary hover:underline"
                  >
                    {company.brregUrl}
                  </a>
                )}
              </DD>
            </GridCol>
          </GridRow>
        </div>
      </CardContent>
    </Card>
  );
};
